import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    verifyPan,
    requestMembershipConsideration,
    getMembershipInviteStatus,
    getMembershipInvites,
    reviewMembershipInvite,
    isApiSuccess,
} from "../../services/api";

// The pan-verify response shape isn't documented here - adjust this one line if it differs.
const isPanVerified = (res) => isApiSuccess(res) || res?.success === true;

const errMsg = (err, fallback) => err?.message || fallback;

// ---- customer: request consideration -------------------------------------

/** Has this customer already requested this plan? */
export const fetchInviteStatus = createAsyncThunk(
    "membershipInvite/fetchStatus",
    async (programId, { rejectWithValue }) => {
        try {
            const res = await getMembershipInviteStatus(programId);
            if (!isApiSuccess(res)) return rejectWithValue(errMsg(res, "Unable to check your request."));
            return { programId, data: res.data };
        } catch (err) {
            return rejectWithValue(errMsg(err, "Unable to check your request."));
        }
    }
);

export const verifyPanThunk = createAsyncThunk(
    "membershipInvite/verifyPan",
    async ({ pan, customerId }, { rejectWithValue }) => {
        try {
            const res = await verifyPan(pan, customerId);
            if (!isPanVerified(res)) {
                return rejectWithValue(res?.message || "We couldn't verify this PAN. Check the number and try again.");
            }
            return { pan };
        } catch (err) {
            return rejectWithValue(errMsg(err, "We couldn't verify this PAN. Try again."));
        }
    }
);

export const submitInviteRequest = createAsyncThunk(
    "membershipInvite/submit",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await requestMembershipConsideration(payload);
            if (isApiSuccess(res)) return { programId: payload.program_id, data: res.data };
            // Server-side duplicate guard (another tab / double submit)
            if (res?.data?.already_requested) {
                return { programId: payload.program_id, alreadyRequested: true, data: { requested: true, ...res.data } };
            }
            return rejectWithValue(res?.message || "Your request could not be sent. Please try again.");
        } catch (err) {
            return rejectWithValue(errMsg(err, "Your request could not be sent. Please try again."));
        }
    }
);

// ---- admin: review list ---------------------------------------------------

export const fetchInvites = createAsyncThunk(
    "membershipInvite/fetchInvites",
    async (params = {}, { rejectWithValue }) => {
        try {
            const res = await getMembershipInvites(params);
            if (!isApiSuccess(res)) return rejectWithValue(errMsg(res, "Unable to load requests."));
            return res.data; // Laravel paginator
        } catch (err) {
            return rejectWithValue(errMsg(err, "Unable to load requests."));
        }
    }
);

export const reviewInviteThunk = createAsyncThunk(
    "membershipInvite/review",
    async ({ id, action, reason = null }, { rejectWithValue }) => {
        try {
            const res = await reviewMembershipInvite(id, action, reason);
            if (!isApiSuccess(res)) return rejectWithValue(errMsg(res, "Unable to update this request."));
            return { id, ...res.data };
        } catch (err) {
            return rejectWithValue(errMsg(err, "Unable to update this request."));
        }
    }
);

const initialState = {
    // customer
    statusByProgram: {}, // program_id -> { requested, invite_status, invite_status_label, rejection_reason }
    statusLoading: false,
    panVerifiedFor: null, // the PAN string that passed verification; editing the PAN makes it stale on its own
    panVerifying: false,
    panError: null,
    submitting: false,
    submitError: null,
    // admin
    list: { rows: [], pagination: null, loading: false, error: null },
    reviewingId: null,
    reviewError: null,
};

const membershipInviteSlice = createSlice({
    name: "membershipInvite",
    initialState,
    reducers: {
        /** Call when the modal opens so a previous attempt doesn't leak into this one */
        resetInviteForm(state) {
            state.panVerifiedFor = null;
            state.panVerifying = false;
            state.panError = null;
            state.submitting = false;
            state.submitError = null;
        },
        clearPanError(state) {
            state.panError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // status
            .addCase(fetchInviteStatus.pending, (state) => {
                state.statusLoading = true;
            })
            .addCase(fetchInviteStatus.fulfilled, (state, { payload }) => {
                state.statusLoading = false;
                state.statusByProgram[payload.programId] = payload.data;
            })
            .addCase(fetchInviteStatus.rejected, (state) => {
                state.statusLoading = false; // the server re-checks on submit, so fail open
            })
            // verify PAN
            .addCase(verifyPanThunk.pending, (state) => {
                state.panVerifying = true;
                state.panError = null;
            })
            .addCase(verifyPanThunk.fulfilled, (state, { payload }) => {
                state.panVerifying = false;
                state.panVerifiedFor = payload.pan;
            })
            .addCase(verifyPanThunk.rejected, (state, { payload }) => {
                state.panVerifying = false;
                state.panVerifiedFor = null;
                state.panError = payload || "We couldn't verify this PAN. Try again.";
            })
            // submit
            .addCase(submitInviteRequest.pending, (state) => {
                state.submitting = true;
                state.submitError = null;
            })
            .addCase(submitInviteRequest.fulfilled, (state, { payload }) => {
                state.submitting = false;
                state.statusByProgram[payload.programId] = payload.alreadyRequested
                    ? payload.data
                    : { requested: true, invite_status: 0, invite_status_label: "Pending" };
            })
            .addCase(submitInviteRequest.rejected, (state, { payload }) => {
                state.submitting = false;
                state.submitError = payload || "Your request could not be sent. Please try again.";
            })
            // admin list
            .addCase(fetchInvites.pending, (state) => {
                state.list.loading = true;
                state.list.error = null;
            })
            .addCase(fetchInvites.fulfilled, (state, { payload }) => {
                state.list.loading = false;
                const { data, ...pagination } = payload;
                state.list.rows = data || [];
                state.list.pagination = pagination;
            })
            .addCase(fetchInvites.rejected, (state, { payload }) => {
                state.list.loading = false;
                state.list.error = payload || "Unable to load requests.";
            })
            // admin review
            .addCase(reviewInviteThunk.pending, (state, { meta }) => {
                state.reviewingId = meta.arg.id;
                state.reviewError = null;
            })
            .addCase(reviewInviteThunk.fulfilled, (state, { payload }) => {
                state.reviewingId = null;
                const row = state.list.rows.find((r) => r.id === payload.id);
                if (row) {
                    row.status = payload.invite_status;
                    row.status_label = payload.invite_status_label;
                }
            })
            .addCase(reviewInviteThunk.rejected, (state, { payload }) => {
                state.reviewingId = null;
                state.reviewError = payload || "Unable to update this request.";
            });
    },
});

export const { resetInviteForm, clearPanError } = membershipInviteSlice.actions;

// selectors
export const selectInviteStatus = (programId) => (state) => state.membershipInvite.statusByProgram[programId];

export default membershipInviteSlice.reducer;